/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {MovedEntitiesEditor} from '@ion/dls/src/editors/MovedEntitiesEditor';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {partialStatusMessageFormatter} from '@ion/dls/src/utils/ComponentUtils';
import {exportMovedDevices} from '../../store/actions/exporter';
import {ExportContext} from '../../types/redux';
import BulkMoveDeviceEditor from './BulkMoveDeviceEditor';
import {
  adminDevicesViewMovedResultsSelector,
  deviceMoveErrorsSelector,
  deviceMoveSelectedDevicesSelector
} from './state-selectors';
import {useDeviceMove} from './useDeviceMove';

/**
 * Holds dialog functionality for moving a device.
 */
export const MoveDeviceDialog = () => {
  const dispatch              = useDispatch();
  const viewMovedDevices      = useSelector(adminDevicesViewMovedResultsSelector);
  const movedDevicesErrors    = useSelector(deviceMoveErrorsSelector);
  const selectedDevicesToMove = useSelector(deviceMoveSelectedDevicesSelector);
  const {pending}             = useSelector(({exporter}) => exporter.current);
  const isExporting           = pending[ExportContext.movedDevices] === true;

  const {
    showMoveDevice,
    movedDevices,
    showSuccess,
    onCloseSnack,
    onViewRecords,
    onCancelViewMovedDevicesDialog
  } = useDeviceMove();

  const onMovedDevicesExportClick = useCallback((items) => {
    dispatch(exportMovedDevices(items) as any);
  }, []);

  return (
    <>
      {showMoveDevice && <BulkMoveDeviceEditor />}
      {viewMovedDevices && <MovedEntitiesEditor entity={translate('common:entityLabels.device')}
        isExporting={isExporting}
        movedEntities={movedDevices}
        errors={movedDevicesErrors}
        onCancel={onCancelViewMovedDevicesDialog}
        onMovedDevicesExportClick={onMovedDevicesExportClick} />}
      {showSuccess && (
        <IonSnackbar show
          message={translate('common:editors.shared.successMoved', {
            entity : translate('common:entityLabels.devices')
          })}
          autoHideDuration={3000}
          partialSuccessState={partialStatusMessageFormatter(movedDevices, selectedDevicesToMove)}
          customDismissLabel={translate('common:dismiss')}
          actionLabel={translate('common:viewRecords')}
          onAction={onViewRecords}
          onDismiss={onCloseSnack} />
      )}
    </>
  );
};

export default memo(MoveDeviceDialog);
